import { useEffect, useState } from 'react';
import type { CallRecordListItem } from '@techgitter/contracts';
import { fetchCalls } from '../services/callsApi';

/** One page, newest first. A job with more calls than this shows its newest. */
export const JOB_CALLS_PAGE_SIZE = 50;

/**
 * Call records linked to one job (`cr001_job_id`, the Dataverse job GUID), for
 * the technician card's job log. Pass a falsy guid for an item with no job —
 * a standalone appointment or a check-in row — and nothing is fetched.
 */
export function useJobCalls(jobGuid: string | null | undefined): {
  data: CallRecordListItem[];
  isLoading: boolean;
  error: string | null;
} {
  const [data, setData] = useState<CallRecordListItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setData([]);
    setError(null);
    if (!jobGuid) {
      setIsLoading(false);
      return;
    }
    const controller = new AbortController();
    setIsLoading(true);
    fetchCalls({
      pageSize: JOB_CALLS_PAGE_SIZE,
      jobId: jobGuid,
      sort: 'startTime',
      order: 'desc',
      signal: controller.signal,
    })
      .then((resp) => setData(resp.items))
      .catch((err) => {
        if (err?.name === 'AbortError') return;
        setError(err instanceof Error ? err.message : 'Failed to load calls');
      })
      .finally(() => {
        if (!controller.signal.aborted) setIsLoading(false);
      });
    return () => controller.abort();
  }, [jobGuid]);

  return { data, isLoading, error };
}
